import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Area, Legend,
} from 'recharts';
import { getAssetTimeline, getPriceHistory, getAssets, getAssetLatest } from '../api';

const PRESETS = [{ label: '1D', days: 1 }, { label: '7D', days: 7 }, { label: '30D', days: 30 }];
const CONF_FILTERS = ['ALL', 'HIGH+MEDIUM', 'HIGH ONLY'];

// Map UI filter labels to backend param values
const CONF_PARAM = { 'ALL': null, 'HIGH+MEDIUM': 'MEDIUM', 'HIGH ONLY': 'HIGH' };

// Rolling average over hourly buckets; requires at least 3 total articles in the window
function rollingAvg(data, field, countField, windowSize = 3, minArticles = 3) {
  return data.map((point, i) => {
    if (point[field] == null) return point;
    const start = Math.max(0, i - windowSize + 1);
    const slice = data.slice(start, i + 1).filter(p => p[field] != null);
    const totalArticles = slice.reduce((s, p) => s + (p[countField] || 0), 0);
    if (totalArticles < minArticles) return { ...point, [field]: null };
    const avg = slice.reduce((s, p) => s + p[field], 0) / slice.length;
    return { ...point, [field]: avg };
  });
}

function formatDate(ts, days) {
  const d = new Date(ts);
  return days <= 1
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const hasSentiment = payload.some(p => p.dataKey === 'Sentiment' && p.value != null);
  const article = payload.find(p => p.payload?.article)?.payload?.article;
  const confColor = { HIGH: '#22c55e', MEDIUM: '#eab308', LOW: '#94a3b8' };
  return (
    <div className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs shadow-xl max-w-[260px]">
      <p className="text-slate-600 mb-1.5">{label}</p>
      {payload.map(p => p.value != null && (
        <div key={p.name} className="flex justify-between gap-4" style={{ color: p.color }}>
          <span>{p.name}:</span>
          <span className="font-semibold tabular-nums">
            {p.value.toFixed(p.name === 'Sentiment' ? 3 : 2)}
          </span>
        </div>
      ))}
      {!hasSentiment && (
        <p className="text-slate-600 italic mt-1">No sentiment — hover a blue dot</p>
      )}
      {hasSentiment && article && (
        <div className="mt-2 pt-2 border-t border-slate-300 space-y-1">
          <p className="text-slate-800 leading-snug line-clamp-3">{article.headline}</p>
          <div className="flex items-center justify-between gap-2 text-slate-400">
            <span className="truncate">{article.source}</span>
            <span className="font-semibold flex-shrink-0" style={{ color: confColor[article.confidence] || '#94a3b8' }}>
              {article.confidence}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


const HELP_ITEMS = [
  {
    color: '#3b82f6',
    label: 'Sentiment line (blue)',
    desc: 'A 3-hour rolling average of all article sentiment scores for the selected asset, plotted on the left axis. Each data point is the average of up to the last 3 hourly buckets of analysed articles.',
  },
  {
    color: '#94a3b8',
    label: 'Price line (grey)',
    desc: 'Hourly closing price from CryptoCompare (crypto) or Yahoo Finance (equities), plotted on the right axis. Scale is auto-fitted to the data range.',
  },
  {
    color: '#22c55e',
    label: 'Green dashed line (+0.2)',
    desc: 'Bullish threshold. When the rolling sentiment average is above this line, the prevailing signal is bullish.',
  },
  {
    color: '#ef4444',
    label: 'Red dashed line (−0.2)',
    desc: 'Bearish threshold. When sentiment falls below this line, the prevailing signal is bearish.',
  },
  {
    color: null,
    label: 'Green / red fill',
    desc: 'The shaded area under the sentiment line is green-tinted in the upper half (positive sentiment) and red-tinted in the lower half (negative sentiment), making direction immediately visible.',
  },
  {
    color: null,
    label: '1D / 7D / 30D presets',
    desc: 'Controls the visible time window. Fetches both price and sentiment data fresh for the selected range.',
  },
  {
    color: null,
    label: 'ALL / HIGH+MEDIUM / HIGH ONLY',
    desc: 'Confidence filter. Restricts sentiment data to articles where the AIs confidence in its analysis meets the selected threshold. Useful for filtering out low-confidence signals.',
  },
  {
    color: null,
    label: 'Lead/lag slider (±72 h)',
    desc: 'Shifts the sentiment line left or right relative to the price line. A positive value ("Sentiment leads by +Xh") tests whether sentiment changes precede price moves by X hours.',
  },
  {
    color: null,
    label: 'Vertical crossing markers',
    desc: 'A faint dashed vertical line appears whenever sentiment crosses the ±0.5 threshold — a strong bullish (green) or bearish (red) signal. These mark moments of particularly decisive articles.',
  },
];

function HelpPanel({ onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute top-10 right-0 z-50 w-80 max-h-[70%] overflow-y-auto rounded-xl border border-slate-300 bg-white shadow-2xl text-xs"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-300">
        <span className="font-semibold text-slate-900 text-sm">How to read this chart</span>
        <button
          onClick={onClose}
          className="text-slate-500 hover:text-slate-900 transition-colors text-base leading-none"
          aria-label="Close help"
        >
          ✕
        </button>
      </div>
      <ul className="divide-y divide-slate-200">
        {HELP_ITEMS.map(item => (
          <li key={item.label} className="px-4 py-3 flex gap-3">
            <span className="mt-0.5 flex-shrink-0 w-2.5 h-2.5 rounded-full border border-current"
              style={{ color: item.color || '#64748b', background: item.color ? item.color + '33' : 'transparent' }} />
            <div>
              <p className="font-semibold text-slate-800 mb-0.5">{item.label}</p>
              <p className="text-slate-600 leading-relaxed">{item.desc}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SentimentPriceChart() {
  const [assets, setAssets] = useState([]);
  const [symbol, setSymbol] = useState('BTC');
  const [days, setDays] = useState(7);
  const [confFilter, setConfFilter] = useState('ALL');
  const [lagHours, setLagHours] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    getAssets().then(d => {
      const list = d.assets || [];
      setAssets(list);
      if (list.length > 0) setSymbol(list[0].symbol);
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true); setWarning(null);
    try {
      const now = new Date();
      const from = new Date(now - days * 86400000).toISOString();
      const minConf = CONF_PARAM[confFilter];
      const [sent, price, latest] = await Promise.all([
        getAssetTimeline(symbol, {
          from_date: from,
          to_date: now.toISOString(),
          ...(minConf && { min_confidence: minConf }),
        }),
        getPriceHistory(symbol, days),
        getAssetLatest(symbol, 500, from),
      ]);
      if (sent.warning) setWarning(sent.warning);

      // Build hourly article map: keep the article with largest |score| per hour
      const articlesByHour = {};
      for (const a of (latest.results || [])) {
        const hr = a.analysed_at.replace(' ', 'T').substring(0, 13);
        if (!articlesByHour[hr] || Math.abs(a.score) > Math.abs(articlesByHour[hr].score)) {
          articlesByHour[hr] = {
            headline: (a.article_text || '').substring(0, 120).replace(/\n/g, ' ').trim() || '(no headline)',
            source: a.source_name || a.source_url || 'Unknown',
            score: a.score,
            confidence: a.confidence,
          };
        }
      }

      const priceMap = {};
      for (const p of (price.prices || [])) priceMap[p.timestamp.substring(0, 13)] = p.price_usd;

      const sentPoints = (sent.data_points || []).map(dp => ({
        hr: new Date(new Date(dp.hour_bucket).getTime() + lagHours * 3600000).toISOString().substring(0, 13),
        v: dp.avg_score,
        count: dp.article_count,
      }));
      const sentByHr = Object.fromEntries(sentPoints.map(p => [p.hr, { v: p.v, count: p.count }]));

      const hours = new Set([...sentPoints.map(p => p.hr), ...Object.keys(priceMap)]);
      const merged = Array.from(hours).sort().map(hr => ({
        label: formatDate(hr + ':00:00Z', days),
        Sentiment: sentByHr[hr]?.v ?? null,
        ArtCount: sentByHr[hr]?.count ?? 0,
        Price: priceMap[hr] ?? null,
        article: articlesByHour[hr] || null,
      }));
      setChartData(rollingAvg(merged, 'Sentiment', 'ArtCount', 3));
    } catch { setChartData([]); }
    finally { setLoading(false); }
  }, [symbol, days, lagHours, confFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const isCrypto = !['AAPL','MSFT','NVDA','TSLA','AMZN','GOOGL','META','NFLX','SPY','QQQ'].includes(symbol);
  const priceFmt = v => v == null ? '' : isCrypto
    ? `$${Math.round(v).toLocaleString('en-US')}`
    : `$${v.toFixed(2)}`;

  const PresetBtn = ({ label: l, active, onClick }) => (
    <button onClick={onClick} className={`bg-white border border-slate-300 px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${active ? 'bg-secondaryLight border-secondaryLight text-white' : 'text-slate-500 hover:border-primary hover:text-primary'}`}>{l}</button>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-4 h-full relative">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-primary tracking-wide">Price vs Sentiment</h2>
        <button
          onClick={() => setHelpOpen(o => !o)}
          className="w-5 h-5 rounded-full border border-slate-300 text-slate-500 hover:border-primary hover:text-primary transition-colors flex items-center justify-center text-[11px] font-bold leading-none"
          aria-label="Help"
        >
          ?
        </button>
      </div>
      {helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}
      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={symbol}
          onChange={e => setSymbol(e.target.value)}
          className="bg-white border border-slate-300 focus:border-primary focus:outline-none rounded-lg text-slate-800 text-sm px-3 py-2 transition-colors w-auto min-w-[200px]"
        >
          {assets.map(a => (
            <option key={a.symbol} value={a.symbol}>
              {a.symbol} — {a.asset_name || a.symbol} ({a.article_count} articles)
            </option>
          ))}
          {assets.length === 0 && <option value="BTC">BTC — Bitcoin</option>}
        </select>

        <div className="flex gap-1">
          {PRESETS.map(p => (
            <PresetBtn key={p.label} label={p.label} active={days === p.days} onClick={() => setDays(p.days)} />
          ))}
        </div>

        <div className="flex gap-1">
          {CONF_FILTERS.map(f => (
            <PresetBtn key={f} label={f} active={confFilter === f} onClick={() => setConfFilter(f)} />
          ))}
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <span className="text-xs text-slate-500">
            Sentiment leads price by{' '}
            <span className="font-semibold text-slate-700">
              {lagHours > 0 ? '+' : ''}{lagHours}h
            </span>
          </span>
          <input type="range" min={-72} max={72} value={lagHours}
            onChange={e => setLagHours(Number(e.target.value))}
            className="cursor-pointer"
            style={{ accentColor: 'var(--color-secondary-light)' }} />
        </div>
      </div>

      {/* Warning */}
      {warning && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-300">
          {warning}
        </div>
      )}

      {/* Chart area */}
      {loading && (
        <div className="flex flex-1 items-center justify-center text-slate-500 text-sm">Loading chart data...</div>
      )}
      {!loading && chartData.length === 0 && (
        <div className="flex flex-1 items-center justify-center text-slate-500 text-sm">
          No data yet for {symbol}. Submit articles or wait for the news poller.
        </div>
      )}
      {!loading && chartData.length > 0 && (() => {
        // Detect ±0.5 sentiment crossings for vertical article markers
        const crossings = chartData.filter((pt, i) => {
          if (pt.Sentiment == null) return false;
          const prev = chartData.slice(0, i).reverse().find(p => p.Sentiment != null);
          if (!prev) return Math.abs(pt.Sentiment) >= 0.5;
          const crossed05  = prev.Sentiment < 0.5  && pt.Sentiment >= 0.5;
          const crossedN05 = prev.Sentiment > -0.5 && pt.Sentiment <= -0.5;
          return crossed05 || crossedN05;
        });

        return (
          <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                {/* domain [-1,+1]: y=0 is exactly 50% of the chart height */}
                <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#22c55e" stopOpacity={0.28} />
                  <stop offset="50%"  stopColor="#22c55e" stopOpacity={0.04} />
                  <stop offset="50%"  stopColor="#ef4444" stopOpacity={0.04} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.28} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} interval="preserveStartEnd" />
              <YAxis yAxisId="s" domain={[-1, 1]} tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} tickFormatter={v => v.toFixed(1)} />
              <YAxis yAxisId="p" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} tickFormatter={priceFmt} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={v => <span className="text-slate-400">{v}</span>} />
              <ReferenceLine yAxisId="s" y={0.2}  stroke="#22c55e" strokeDasharray="3 3" strokeWidth={1} />
              <ReferenceLine yAxisId="s" y={-0.2} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={1} />
              <ReferenceLine yAxisId="s" y={0}    stroke="#334155" strokeWidth={1} />
              {crossings.map(pt => (
                <ReferenceLine key={pt.label} x={pt.label} yAxisId="s"
                  stroke={pt.Sentiment >= 0.5 ? '#22c55e' : '#ef4444'}
                  strokeDasharray="2 4" strokeWidth={1} strokeOpacity={0.6} />
              ))}
              <Area yAxisId="s" type="monotone" dataKey="Sentiment"
                stroke="#3b82f6" strokeWidth={2} fill="url(#sentGrad)"
                dot={{ r: 4, fill: '#3b82f6', strokeWidth: 0 }}
                activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 1.5 }}
                connectNulls />
              <Line yAxisId="p" type="monotone" dataKey="Price"
                stroke="#94a3b8" strokeWidth={1.5} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
          </div>
        );
      })()}
    </div>
  );
}
